import { useCopyToClipboard } from "@src/global_functions/hooks";
import { Icon, Text, Tooltip } from "@src/straps/base";
import { ComponentProps, ReactNode, useState } from "react";

type Props = {
  children?: ReactNode;
  buttonChild?: ReactNode;
  value: string;
  onCopiedMessage?: string;
  onHoveredMessage?: string;
  iconBackgroundColor?: `#${string}` | null;
  alignTooltip?: Exclude<
    ComponentProps<typeof Tooltip>["align"],
    "left" | "right" | "bottom"
  >;
  ["data-testid"]?: string;
} & ComponentProps<typeof Text>;

const CopyableContent = ({
  children,
  buttonChild = <Icon name="copy" size="small" color="secondary" />,
  value,
  onCopiedMessage,
  onHoveredMessage,
  iconBackgroundColor = "#FFFFFF",
  alignTooltip = "topLeft",
  "data-testid": dataTestId,
  ...textProps
}: Readonly<Props>) => {
  const { isCopied, handleCopy, setIsCopied } = useCopyToClipboard({
    resetInterval: 3000,
  });
  const [isHovered, setIsHovered] = useState(false);

  const props = {
    value,
    onCopiedMessage,
    onHoveredMessage,
    alignTooltip,
    dataTestId,
    isHovered,
    setIsHovered,
    isCopied,
    handleCopy,
    setIsCopied,
  };

  if (!children)
    return (
      <CopyButtonWrapper
        {...props}
        className="m-0 inline-flex w-fit cursor-pointer bg-transparent p-0"
      >
        {buttonChild}
      </CopyButtonWrapper>
    );

  return (
    <span className="group/content relative inline-block">
      <CopyButtonWrapper
        {...props}
        style={{
          background: iconBackgroundColor
            ? `linear-gradient(to right, ${iconBackgroundColor}E6 70%, ${iconBackgroundColor}CC 80%, ${iconBackgroundColor}80 90%, ${iconBackgroundColor}4D 100%)`
            : "transparent",
        }}
        className="absolute -right-2 top-0 flex h-full translate-x-0 cursor-pointer items-center justify-center overflow-hidden rounded-full opacity-0 transition-all will-change-transform group-hover/content:right-0 group-hover/content:translate-x-full group-hover/content:pl-1 group-hover/content:pr-2 group-hover/content:opacity-100"
      >
        {buttonChild}
      </CopyButtonWrapper>
      {typeof children === "string" || typeof children === "number" ? (
        <Text as="span" {...textProps}>
          {children}
        </Text>
      ) : (
        children
      )}
    </span>
  );
};

export default CopyableContent;

export const CopyButton = ({
  children,
  ...props
}: Omit<Props, "buttonChild">) => (
  <CopyableContent {...props} buttonChild={children} />
);

function CopyButtonWrapper({
  value,
  children,
  isHovered,
  setIsHovered,
  isCopied,
  setIsCopied,
  handleCopy,
  onCopiedMessage,
  onHoveredMessage,
  className,
  dataTestId,
  style,
  alignTooltip,
}: Readonly<
  Omit<Props, "className" | "style" | "data-testid" | "iconBackgroundColor"> &
    ReturnType<typeof useCopyToClipboard> &
    Pick<ComponentProps<"button">, "className" | "style"> & {
      isHovered: boolean;
      setIsHovered: (next: boolean) => void;
      dataTestId?: string;
    }
>) {
  return (
    <Tooltip
      content={
        <TooltipContent
          isHovered={isHovered}
          isCopied={isCopied}
          onCopiedMessage={onCopiedMessage}
          onHoveredMessage={onHoveredMessage}
        />
      }
      fixed
      align={alignTooltip}
    >
      <button
        onClick={() => handleCopy(value)}
        onMouseEnter={() => setIsHovered(true)}
        data-testid={dataTestId ?? "click-to-copy-container"}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsCopied(false);
        }}
        style={style}
        className={className}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function TooltipContent({
  isCopied,
  isHovered,
  onCopiedMessage,
  onHoveredMessage,
}: Readonly<{
  isCopied: boolean;
  isHovered: boolean;
  onCopiedMessage?: string;
  onHoveredMessage?: string;
}>) {
  const showTooltip = isCopied || isHovered;
  return showTooltip ? (
    <div className="flex items-center gap-1 align-middle">
      <Icon
        name={isCopied ? "check" : "copy"}
        size="small"
        color={isCopied ? "positive" : "secondary"}
        className="mb-px"
      />
      <Text variant="sb_t-12-500" className="truncate bg-opacity-20">
        {isCopied ? onCopiedMessage ?? "Copied to clipboard!" : null}
        {isHovered && !isCopied ? onHoveredMessage ?? "Click to copy" : null}
      </Text>
    </div>
  ) : null;
}
